/** Pure helpers for the Schedule flow: which versions can go out, the default account, the default selection. */
import type { Account, Category, Publication, Queue, SeriesDetail } from "../../api/types";

export type Candidate = { version_id: string; episode_id: string; sequence: number; title: string; language_code: string; version: number; imported: boolean; created_at?: string };

/** The latest approved version per episode × language, in episode order. */
export function approvedCandidates(d: SeriesDetail): Candidate[] {
  const out: Candidate[] = [];
  for (const e of [...d.episodes].sort((a, b) => a.sequence - b.sequence)) {
    const byLang = new Map<string, (typeof e.versions)[number]>();
    for (const v of e.versions) {
      if (v.status !== "approved") continue;
      const cur = byLang.get(v.language_code);
      if (!cur || v.version > cur.version) byLang.set(v.language_code, v);
    }
    for (const v of byLang.values()) out.push({ version_id: v.id, episode_id: e.id, sequence: e.sequence, title: e.title, language_code: v.language_code, version: v.version, imported: v.origin === "imported", created_at: v.created_at });
  }
  return out;
}

/** Languages present among the candidates, English first, then by count. */
export function languagesOf(c: Candidate[]): string[] {
  const n = new Map<string, number>();
  for (const x of c) n.set(x.language_code, (n.get(x.language_code) ?? 0) + 1);
  return [...n.keys()].sort((a, b) => (a === "en" ? -1 : b === "en" ? 1 : (n.get(b)! - n.get(a)!) || a.localeCompare(b)));
}

/** version_id → the accounts it already has a live-or-pending publication on (anything but cancelled). */
export function accountsByVersion(pubs: Publication[]): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>();
  for (const p of pubs) {
    if (p.state === "cancelled") continue;
    const s = m.get(p.version_id) ?? new Set<string>();
    s.add(p.account_id);
    m.set(p.version_id, s);
  }
  return m;
}

const norm = (s: string) => s.trim().toLowerCase();
/** Category values arrive as a slug from some endpoints and a display name from others; compare through the taxonomy. */
export function sameCategory(a: string | null | undefined, b: string | null | undefined, cats: Category[] = []): boolean {
  if (!a || !b) return false;
  const slug = (x: string) => cats.find((c) => norm(c.slug) === norm(x) || norm(c.name) === norm(x))?.slug ?? norm(x);
  return slug(a) === slug(b);
}

export const isUsable = (a: Account) => a.platform === "youtube" && a.enabled && (a.auth_status === "connected" || a.auth_status === "expiring");

/** Connected YouTube account for this language + category, else for this language. Never a wrong-language default: the owner picks. */
export function defaultAccount(accounts: Account[], lang: string, category: string | null | undefined, cats: Category[] = []): Account | undefined {
  const ok = accounts.filter(isUsable);
  return ok.find((a) => a.language_code === lang && sameCategory(a.category, category, cats)) ?? ok.find((a) => a.language_code === lang);
}

/** Default selection: every candidate in `lang` not already on `accountId` (with no default account: not already posted anywhere). */
export function defaultSelection(c: Candidate[], lang: string, active: Map<string, Set<string>>, accountId: string | undefined): string[] {
  return c.filter((x) => x.language_code === lang && !(accountId ? active.get(x.version_id)?.has(accountId) : active.has(x.version_id))).map((x) => x.version_id);
}

/** The queue a queue-mode plan will use for this account. */
export function queueFor(queues: Queue[], account: Account | undefined, cats: Category[] = []): Queue | undefined {
  if (!account) return undefined;
  return queues.find((q) => q.language_code === account.language_code && sameCategory(q.category, account.category, cats));
}

/** Default explicit times for "Pick times": tomorrow 09:00 local, one a day. */
export function defaultTimes(ids: string[], now = new Date()): Record<string, string> {
  const pad = (n: number) => String(n).padStart(2, "0");
  const out: Record<string, string> = {};
  ids.forEach((id, i) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1 + i, 9, 0);
    out[id] = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
  });
  return out;
}

/** A backfill of old videos (≥ 5 at once, or any approved > 14 days ago): suggest not notifying subscribers (the API's own default for such plans). */
export function looksLikeBackfill(selected: Candidate[], now = Date.now()): boolean {
  return selected.length >= 5 || selected.some((c) => c.created_at && now - Date.parse(c.created_at) > 14 * 86400_000);
}

export const AUDIT_WARNING = "YouTube hasn't audited this app yet — public uploads would be locked private, so these will wait until the audit passes.";
export const isAuditWarning = (w: string) => /audit/i.test(w);
